import React, { useState, useMemo } from 'react';
import {
  Box, Paper, Typography, TextField, InputAdornment,
  Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Pagination, Chip, Stack
} from '@mui/material';
import { Search, Car } from 'lucide-react';

import * as XLSX from 'xlsx'; // npm i xlsx
import jsPDF from 'jspdf'; // npm i jspdf
import 'jspdf-autotable'; // npm i jspdf-autotable

// Dummy data - replace with API later
const dummyRows = [
  { id: '01', ticketId: '1001', name: 'Rodrigue', mobile: '7842030852', enquiry: 'I News help', status: 'Closed', date: '2024-05-07 03:58 PM' },
  { id: '02', ticketId: '1002', name: 'John', mobile: '7842030853', enquiry: 'Booking Issue', status: 'Active', date: '2024-05-07 04:58 PM' },
  { id: '03', ticketId: '1003', name: 'Sarah', mobile: '7842030854', enquiry: 'Payment', status: 'Closed', date: '2024-05-07 05:58 PM' },
  { id: '04', ticketId: '1004', name: 'Mike', mobile: '7842030855', enquiry: 'Driver', status: 'Closed', date: '2024-05-08 03:58 PM' },
  { id: '05', ticketId: '1005', name: 'Anna', mobile: '7842030856', enquiry: 'Refund', status: 'Active', date: '2024-05-08 04:58 PM' },
];

const PAGE_SIZE = 5;
const headers = ['ID', 'TICKET ID', 'NAME', 'MOBILE', 'ENQUIRY', 'STATUS', 'DATE'];

const CustomerCarePortal = () => {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  // 1. Search + Pagination Logic
  const filteredRows = useMemo(() => {
    if (!search.trim()) return dummyRows;
    const q = search.toLowerCase();
    return dummyRows.filter((r) =>
      Object.values(r).some((v) => String(v).toLowerCase().includes(q))
    );
  }, [search]);

  const paginatedRows = filteredRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const totalPages = Math.ceil(filteredRows.length / PAGE_SIZE) || 1;
  const startItem = filteredRows.length === 0? 0 : (page - 1) * PAGE_SIZE + 1;
  const endItem = Math.min(page * PAGE_SIZE, filteredRows.length);

  // 2. Export Logic - Only this added
  const handleExport = (type) => {
    const data = filteredRows; // export all filtered rows
    if (data.length === 0) return alert("No data to export");

    if (type === 'Copy') {
      const text = [headers.join('\t'),...data.map(r => Object.values(r).join('\t'))].join('\n');
      navigator.clipboard.writeText(text);
      alert("Copied to clipboard!");
    }

    if (type === 'CSV') {
      const csv = [headers.join(','),...data.map(r => Object.values(r).join(','))].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url; link.download = 'CustomerCare.csv'; link.click();
      URL.revokeObjectURL(url);
    }

    if (type === 'Excel') {
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'CustomerCare');
      XLSX.writeFile(wb, 'CustomerCare.xlsx');
    }

    if (type === 'PDF') {
      const doc = new jsPDF();
      doc.text("Customer Care Portal", 14, 16);
      doc.autoTable({
        head: [headers],
        body: data.map(r => Object.values(r)),
        startY: 20,
        theme: 'grid',
        styles: { fontSize: 8 }
      });
      doc.save('CustomerCare.pdf');
    }
  };

  const getStatusChip = (status) => {
    const isActive = status === "Active";
    return (
      <Chip
        size="small"
        label={
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
            <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: isActive? "#EF4444" : "#10B981" }} />
            {status}
          </Box>
        }
        sx={{
         ...statusChipSx,
          bgcolor: isActive? "#FEE2E2" : "#D1FAE5",
          color: isActive? "#B91C1C" : "#065F46",
        }}
      />
    );
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'none', p: 3, fontFamily: '"Poppins", "Inter", sans-serif' }}>

      {/* Main WHITE Card */}
      <Paper
        elevation={0}
        sx={{
          mt: 2,
          borderRadius: '22px',
          border: "none",
          backgroundColor: 'none',
          p: 3,
          fontFamily: '"Poppins", "Inter", sans-serif',
        }}
      >
        {/* Header Row */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Typography sx={{ fontSize: 16, fontFamily: 'Poppins', fontWeight: 600, color: '#202122' }}>
            Customer Care Portal
          </Typography>
          <Typography sx={{ fontSize: 13, color: '#6B7280' }}>
            Admin <Box component="span" sx={{ mx: 0.5 }}>/</Box>
            <Box component="span" sx={{ color: '#111827', fontFamily: 'Poppins', fontWeight: 600 }}>Customer Care Portal</Box>
          </Typography>
        </Box>

        {/* Search + Export Buttons Row */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5, flexWrap: 'wrap', gap: 2 }}>
          <TextField
            placeholder="Search"
            size="small"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }} // reset page on search
            sx={{
              width: 380,
              '&.MuiOutlinedInput-root': {
                height: 46,
                borderRadius: '40px',
                background: '#fff',
                fontSize: 14,
                '& fieldset': { borderColor: '#E5E7EB' },
              },
            }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Search size={16} color="#9CA3AF" /></InputAdornment>,
            }}
          />
          <Stack direction="row" spacing={0} sx={{ border: '1px solid #111827', borderRadius: 1.5, overflow: 'hidden' }}>
            {['Copy', 'Excel', 'CSV', 'PDF'].map((btn) => (
              <Button
                key={btn}
                onClick={() => handleExport(btn)} // added this
                sx={{
                  backgroundColor: '#111827',
                  color: '#fff',
                  fontSize: 12,
                  fontWeight: 500,
                  px: 2.5,
                  py: 1.8,
                  textTransform: 'none',
                  borderRadius: 0,
                  minWidth: 70,
                  borderRight: btn!== 'PDF'? '1px solid #333' : 'none',
                  '&:hover': { backgroundColor: '#000' },
                }}
              >
                {btn}
              </Button>
            ))}
          </Stack>
        </Box>

        {/* Table */}
        <TableContainer sx={{ border: 'none', borderRadius: '12px', background: '#fff' }}>
          <Table sx={{ minWidth: 750 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#F9FAFB' }}>
                {headers.map((header) => (
                  <TableCell key={header} sx={tableHeadCellSx}>{header}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedRows.length === 0? (
                <TableRow><TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>No results found</TableCell></TableRow>
              ) : paginatedRows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell sx={tableBodyCellSx}>{row.id}</TableCell>
                  <TableCell sx={tableBodyCellSx}>{row.ticketId}</TableCell>
                  <TableCell sx={tableBodyCellSx}>{row.name}</TableCell>
                  <TableCell sx={tableBodyCellSx}>{row.mobile}</TableCell>
                  <TableCell sx={tableBodyCellSx}>{row.enquiry}</TableCell>
                  <TableCell sx={tableBodyCellSx}>{getStatusChip(row.status)}</TableCell>
                  <TableCell sx={tableBodyCellSx}>{row.date}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Footer Pagination */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3 }}>
          <Typography sx={{ fontSize: 13, color: '#6B7280' }}>
            Showing {startItem} to {endItem} of {filteredRows.length} results
          </Typography>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(e, val) => setPage(val)}
            shape="rounded"
            sx={{
              '&.MuiPaginationItem-root': { fontSize: 13, fontWeight: 500, minWidth: 32, height: 32, color: '#374151' },
              '&.Mui-selected': { backgroundColor: '#111827!important', color: '#fff', borderRadius: 1 },
            }}
          />
        </Box>
      </Paper>
    </Box>
  );
};

// Reusable SX
const tableHeadCellSx = {
  fontSize: 11,
  fontWeight: 600,
  color: '#6B7280',
  letterSpacing: 0.3,
  py: 1.5,
  border: 'none',
  textTransform: 'uppercase',
  backgroundColor: '#fff',
}

const tableBodyCellSx = {
  fontSize: 14,
  fontWeight: 500,
  color: '#111827',
  py: 1.8,
  borderBottom: '1px solid #F3F4F6',
  backgroundColor: '#fff',
}

const statusChipSx = {
  fontSize: 11,
  fontWeight: 700,
  height: 24,
  borderRadius: 16,
  px: 1.5
}

export default CustomerCarePortal;